import { ImageProps } from '@design-system/image';
import { ExtendableElement } from '@design-system/types';
import classNames from 'classnames';
import { GatsbyImage, GatsbyImageProps, getImage } from 'gatsby-plugin-image';
import React, { ReactElement } from 'react';

import { Image } from './models';

// mapper to alt ignores the placeholder added by the migration tool,
// there because the field was required a placeholder value was inserted value placeholder/to-be-filled
// if placeholder value found, replaces it with empty alt value (as decorative)
export const altMapper = (alt: string) => {
  if (typeof alt !== 'string') {
    return alt;
  }

  return ['placeholder', 'to-be-filled'].some(
    (item) => item === alt.toLowerCase()
  )
    ? ''
    : alt;
};

export const imageMapperRaw = (props: Image) => {
  const {
    image,
    rounded,
    aspectRatio,
    objectFit,
    objectPosition,
    alt,
    ...htmlProperties
  } = props || {};

  if ('url' in image) {
    return {
      aspectRatio,
      objectFit,
      objectPosition,
      rounded,
      src: image?.url,
      alt: altMapper(alt),
      ...htmlProperties,
    };
  }

  return undefined;
};

export const gatsbyImageMapper = (
  {
    image,
    rounded,
    aspectRatio,
    objectFit,
    objectPosition,
    alt,
    className,
    ...htmlProperties
  }: Image,
  eagerLoad?: boolean
): ReactElement | undefined => {
  const gatsbyImage = getImage(image);

  if (!gatsbyImage) {
    return undefined;
  }

  const imageClassName = classNames(className, 'cds-image', {
    [`cds-image--${objectFit}`]: objectFit,
    [`cds-image--rounded`]: rounded,
  });

  return (
    <GatsbyImage
      image={gatsbyImage}
      alt={altMapper(alt)}
      className={imageClassName}
      imgClassName={imageClassName}
      style={{
        aspectRatio: aspectRatio as string,
      }}
      objectFit={objectFit as GatsbyImageProps['objectFit']}
      objectPosition={objectPosition as GatsbyImageProps['objectPosition']}
      {...{
        ...htmlProperties,
        ...(eagerLoad ? { loading: 'eager' } : undefined),
      }}
      sizes="(max-width: 300px) 300px, 100vw"
    />
  );
};

export const imageMapper = (props: Image, eagerLoad?: boolean) => {
  if (!props?.image) {
    return undefined;
  }

  if ('gatsbyImageData' in props.image && props.image.gatsbyImageData) {
    return {
      element: gatsbyImageMapper(props, eagerLoad),
    } as ExtendableElement<ImageProps>;
  }

  return {
    props: {
      ...imageMapperRaw(props),
    },
  } as ExtendableElement<ImageProps>;
};
